import { FacilityAsset, FacilityAssessment, RatingLabel, SystemKey, VisualRating, OperationalRating, MaintenanceRating, SystemData, CriterionKey } from '../../models/types';

/* Excel scores age and remaining service life off 5-year dropdowns, not free numbers: M7 and M10
   are validated against $C$8:$I$8 = {0,5,10,15,20,25,30}, and M8/M11 map only those exact values
   (anything else yields "" and the whole score goes blank).

   We keep exact numeric entry, because the true age of a unit is worth recording, but score the
   band the assessor would have picked in Excel. Interpolating instead — which this previously did —
   produces point totals Paul's workbook cannot produce, so the two would never tie out.

   Bands are the boundaries themselves, chosen by nearest with ties going to the harsher band
   (older age, shorter life), so a borderline unit is never flattered. */
export const AGE_BANDS = [0, 5, 10, 15, 20, 25, 30] as const;

const AGE_POINTS: Record<number, number> = { 0: 20, 5: 15, 10: 10, 15: 5, 20: 2.5, 25: 0, 30: 0 };
const RSL_POINTS: Record<number, number> = { 0: 0, 5: 2.5, 10: 5, 15: 7.5, 20: 10, 25: 15, 30: 20 };

/** The 5-year band a raw value scores as. Exported so the UI can show which band applied. */
export function ageBandFor(years: number): number {
  if (!Number.isFinite(years) || years <= 0) return 0;
  if (years >= 30) return 30;
  let best: number = AGE_BANDS[0];
  for (const b of AGE_BANDS) {
    // Ascending with <=, so the larger of two equidistant bands wins: for age, more years is
    // the harsher reading (age 2.5 scores as the 5-year band, not the 0-year band).
    if (Math.abs(b - years) <= Math.abs(best - years)) best = b;
  }
  return best;
}

export function rslBandFor(years: number | null | undefined): number {
  if (years == null || !Number.isFinite(years) || years <= 0) return 0;
  if (years >= 30) return 30;
  let best: number = AGE_BANDS[AGE_BANDS.length - 1];
  for (const b of [...AGE_BANDS].reverse()) {
    // Descending with <=, so the smaller of two equidistant bands wins: for remaining life,
    // fewer years is the harsher reading (2.5 years left scores as the 0-year band).
    if (Math.abs(b - years) <= Math.abs(best - years)) best = b;
  }
  return best;
}

/** Unknown age (null) scores 0 — never the 20 points a "0 years old" default would award. */
function getAgePoints(age: number | null): number {
  return age == null ? 0 : AGE_POINTS[ageBandFor(age)];
}

function getRSLPoints(rsl: number | null | undefined): number {
  return RSL_POINTS[rslBandFor(rsl)];
}

const VISUAL_MAP: Record<VisualRating, number> = {
  'New': 30, 'Like New': 20, 'Good': 10, 'Average': 5, 'Poor': -5, 'Critical': -10, 'Failed': 0,
};
const OPERATIONAL_MAP: Record<OperationalRating, number> = {
  'Fully Functional': 20, 'Minor Issues': -5, 'Major Issues': -10, 'Failed': 0,
};
const MAINTENANCE_MAP: Record<MaintenanceRating, number> = {
  'Above Average': 5, 'Average': 2, 'Below Average': 0, 'None': 0,
};
/** Cleared/unknown maintenance (null) scores 0, same as 'None'/'Below Average'. */
const maintPoints = (m: MaintenanceRating | null): number => (m == null ? 0 : MAINTENANCE_MAP[m]);

export function calculateAssetScore(asset: FacilityAsset): number {
  // Not assessed yet (no visual or no operational rating) → no meaningful score. Callers gate on
  // isAssetAssessed / scoreAsset and never surface this 0; it only guards the maps below from a
  // null key (which would make the whole sum NaN). See scoreAsset.
  if (asset.visualInspection == null || asset.operationalTest == null) return 0;
  // Failed (either) → 0 per the methodology doc. Deliberate deviation from the Excel:
  // its M25 formula scores vis=Failed + op=Major Issues as 7.5 while its rating formula says 'Failed'.
  if (asset.operationalTest === 'Failed' || asset.visualInspection === 'Failed') return 0;
  // Forced downgrades per the source Excel scorecard (Mechanical!M25 precedence):
  // any operational issue or Poor/Critical visual pins the score, regardless of age/RSL/maintenance.
  if (asset.operationalTest === 'Major Issues') return 7.5;
  if (asset.visualInspection === 'Critical') return 7.5;
  if (asset.visualInspection === 'Poor') return 12.5;
  if (asset.operationalTest === 'Minor Issues') return 12.5;
  const sum = getAgePoints(asset.age) + getRSLPoints(asset.remainingServiceLife)
    + VISUAL_MAP[asset.visualInspection] + OPERATIONAL_MAP[asset.operationalTest]
    + maintPoints(asset.maintenanceRecords) + maintPoints(asset.maintenanceServiceLevel);
  return Math.max(0, Math.min(100, sum));
}

/* Paul's scorecard prints a "Points Awarded/Deducted" cell under every condition selection, and
   techs use it to sanity-check a score in the field. Same six values the sum above uses, exposed
   for display. `pinned` marks the case where a forced downgrade fixed the score and these
   contributions never reached the total — showing them without that flag would be misleading. */
export interface CriterionPoints {
  key: CriterionKey;
  label: string;
  points: number;
  max: number;
  /** For age/RSL: the 5-year band the entered value scored as, when it isn't the value itself. */
  band?: number;
}

export function getCriterionPoints(asset: FacilityAsset): { rows: CriterionPoints[]; pinned: boolean } {
  const ageBand = asset.age == null ? 0 : ageBandFor(asset.age);
  const rslBand = rslBandFor(asset.remainingServiceLife);
  const rows: CriterionPoints[] = [
    { key: 'age', label: 'Equipment Age', points: getAgePoints(asset.age), max: 20,
      band: asset.age == null || ageBand === asset.age ? undefined : ageBand },
    { key: 'remainingServiceLife', label: 'Remaining Service Life', points: getRSLPoints(asset.remainingServiceLife), max: 20,
      band: asset.remainingServiceLife == null || rslBand === asset.remainingServiceLife ? undefined : rslBand },
    { key: 'visualInspection', label: 'Visual Inspection', points: asset.visualInspection == null ? 0 : VISUAL_MAP[asset.visualInspection], max: 30 },
    { key: 'operationalTest', label: 'Operational Tests', points: asset.operationalTest == null ? 0 : OPERATIONAL_MAP[asset.operationalTest], max: 20 },
    { key: 'maintenanceRecords', label: 'Maintenance Records', points: maintPoints(asset.maintenanceRecords), max: 5 },
    { key: 'maintenanceServiceLevel', label: 'Maintenance Service Level', points: maintPoints(asset.maintenanceServiceLevel), max: 5 },
  ];
  return { rows, pinned: getDowngradeNote(asset) !== null };
}

/* The scorecard only prompts for an issue list on the ratings that carry a deduction — Excel
   N13/N16 on each system tab. Both prompts are conditional there; the app previously showed the
   visual one unconditionally and had no operational one at all. */
export const VISUAL_RATINGS_NEEDING_ISSUES: VisualRating[] = ['Poor', 'Critical', 'Failed'];
export const OPERATIONAL_RATINGS_NEEDING_ISSUES: OperationalRating[] = ['Minor Issues', 'Major Issues', 'Failed'];

export function visualNeedsIssues(v: VisualRating): boolean {
  return VISUAL_RATINGS_NEEDING_ISSUES.includes(v);
}
export function operationalNeedsIssues(o: OperationalRating): boolean {
  return OPERATIONAL_RATINGS_NEEDING_ISSUES.includes(o);
}

/* Excel K24 equivalent — explains why a score was pinned by a forced downgrade. Wording and
   precedence follow K24 exactly, with one deliberate addition: K24 emits nothing for
   visual = Critical even though that pins the score to 7.5, which leaves the worst-but-one
   rating unexplained. We keep a note there rather than reproduce the omission. */
export function getDowngradeNote(asset: FacilityAsset): string | null {
  if (asset.operationalTest === 'Failed' || asset.visualInspection === 'Failed')
    return 'Failed Equipment';
  if (asset.operationalTest === 'Major Issues')
    return 'Unit Rating Downgraded Due to Major Operational Issues';
  if (asset.operationalTest === 'Minor Issues')
    return 'Unit Rating Downgraded Due to Minor Operational Issues';
  if (asset.visualInspection === 'Poor')
    return 'Unit Rating Downgraded Due to Visual Inspection Issues';
  // Not in K24 — see note above.
  if (asset.visualInspection === 'Critical')
    return 'Unit Rating Downgraded Due to Visual Inspection Issues';
  return null;
}

/* Equipment Ratings!G3:H9 — the "Operational Condition" column the per-asset form reads via R25.
   Distinct from RATING_DESCRIPTIONS below, which is the A3:C9 "Descriptive" column used for the
   overall/summary legend. The two sets differ in wording and the asset card must use this one. */
export const ASSET_CONDITION_TEXT: Record<RatingLabel, string> = {
  New: 'New or Recently Installed.',
  'Like New': 'No Observed or Reported Issues, No Failures Expected.',
  Good: 'No Major Issues.',
  Average: 'Operational Condition',
  Poor: 'Repairs Needed to Continue Operation.',
  Critical: 'Major Repairs Needed or End of Life Cycle.',
  Failed: 'Immediate Replacement or Repair Required.',
};

/* Excel R25: the condition text is overridden when the asset is past its expected life or has
   major operational issues, so those two facts win over the rating-derived sentence. */
export function getAssetConditionText(asset: FacilityAsset, rating: RatingLabel): string {
  if (asset.remainingServiceLife === 0) return 'Past Expected Lifetime';
  if (asset.operationalTest === 'Major Issues') return 'Major Issues Observed';
  return ASSET_CONDITION_TEXT[rating];
}

export function getAssetRating(score: number): RatingLabel {
  if (score <= 5) return 'Failed';
  if (score <= 10) return 'Critical';
  if (score <= 25) return 'Poor';
  if (score <= 50) return 'Average';
  if (score <= 65) return 'Good';
  if (score <= 80) return 'Like New';
  return 'New';
}

/* The building rating is a percentage of the *achievable* score, not of a fixed 400. Each
   assessed system contributes up to 100 points, so a building where only some systems were
   assessed is judged against its own smaller ceiling (maxPossibleScore) — otherwise a fully
   sound mechanical-only building would read "Poor" against 400. maxPossible defaults to 400 so
   the thresholds below are identical to the old fixed-scale ones (20/40/100/200/260/320 of 400
   are 5/10/25/50/65/80%), and any legacy caller passing a raw 0–400 score is unaffected. */
export function getBuildingRating(score: number, maxPossible = 400): RatingLabel {
  const pct = maxPossible > 0 ? (score / maxPossible) * 100 : 0;
  if (pct <= 5) return 'Failed';
  if (pct <= 10) return 'Critical';
  if (pct <= 25) return 'Poor';
  if (pct <= 50) return 'Average';
  if (pct <= 65) return 'Good';
  if (pct <= 80) return 'Like New';
  return 'New';
}

/** Both core condition judgments are made — visual inspection and operational test. Age, RSL and
 *  maintenance carry neutral defaults, so these two are the signal that a human has actually
 *  evaluated the unit. Until then the asset has no score and no rating (blank) rather than the
 *  ~"Good" its defaults would imply — the same "don't assert a value before assessment" rule RSL
 *  follows. */
export function isAssetAssessed(asset: FacilityAsset): boolean {
  return asset.visualInspection != null && asset.operationalTest != null;
}

/** Score + rating for an asset, or an empty object when it hasn't been assessed yet. Spread onto
 *  the asset so an unassessed one carries NO score/rating keys — a nested `undefined` would be
 *  rejected by the Firestore write, and absence is what the UI reads as "not assessed". */
export function scoreAsset(asset: FacilityAsset): { score?: number; rating?: RatingLabel } {
  if (!isAssetAssessed(asset)) return {};
  const score = calculateAssetScore(asset);
  return { score, rating: getAssetRating(score) };
}

/** Recompute an asset's derived score/rating from its current fields, removing them when it isn't
 *  assessed. Never trusts a stored score, so no stale rating lingers after an edit. */
export function withDerived(asset: FacilityAsset): FacilityAsset {
  const next: FacilityAsset = { ...asset };
  const d = scoreAsset(asset);
  if (d.score === undefined) { delete next.score; delete next.rating; }
  else { next.score = d.score; next.rating = d.rating; }
  return next;
}

/** Rating-filter bucket: the asset's rating, or 'unassessed' until visual + operational are both set. */
export type RatingBucket = RatingLabel | 'unassessed';

/** Filter chip order — most severe first, unassessed last. */
export const RATING_BUCKETS: RatingBucket[] = ['Failed', 'Critical', 'Poor', 'Average', 'Good', 'Like New', 'New', 'unassessed'];

/** "Needs attention" shortcut — the same Failed/Critical/Poor grouping generateRecommendations uses. */
export const NEEDS_ATTENTION: RatingLabel[] = ['Failed', 'Critical', 'Poor'];

export function ratingBucket(asset: FacilityAsset): RatingBucket {
  return scoreAsset(asset).rating ?? 'unassessed';
}

export function calculateSystemData(assets: FacilityAsset[]): SystemData {
  const scored = assets.map(withDerived);
  /* Only assessed assets have a score; a system's score is their average. A system that is empty —
     OR that has assets but none assessed yet — is "Not assessed", not a zero score: leaving
     score/rating off (rather than score 0 → rating "Failed") is what the summary cards read to show
     "Not assessed". Omit the keys entirely — a nested `undefined` would be rejected by Firestore. */
  const assessed = scored.filter(a => a.score !== undefined);
  if (assessed.length === 0) return { assets: scored };
  const score = assessed.reduce((s, a) => s + (a.score ?? 0), 0) / assessed.length;
  return { assets: scored, score, rating: getAssetRating(score) };
}

export function calculateOverallScore(assessment: FacilityAssessment): number {
  const systems: SystemData[] = [assessment.controls, assessment.electrical, assessment.mechanical, assessment.plumbing];
  const active = systems.filter(s => s.assets.some(isAssetAssessed));
  if (active.length === 0) return 0;
  return active.reduce((sum, s) => sum + (s.score ?? 0), 0);
}

/* How many of the four system types have at least one *assessed* asset. This is what "assessed"
   means everywhere the overall score is scaled: a system with no assessed asset contributes
   nothing and is excluded from the achievable total, so a facility with nothing assessed scales
   against 0 (and reads "Not assessed") rather than being judged "Failed" against a full ceiling. */
export function activeSystemCount(assessment: FacilityAssessment): number {
  const systems: SystemData[] = [assessment.controls, assessment.electrical, assessment.mechanical, assessment.plumbing];
  return systems.filter(s => s.assets.some(isAssetAssessed)).length;
}

/** How many assets in an assessment have actually been assessed (have a score/rating). This — not
   the raw asset count — is what reports mean by "assets assessed". */
export function assessedAssetCount(assessment: FacilityAssessment): number {
  const systems: SystemData[] = [assessment.controls, assessment.electrical, assessment.mechanical, assessment.plumbing];
  return systems.reduce((n, s) => n + s.assets.filter(isAssetAssessed).length, 0);
}

/** Every asset recorded for an assessment, assessed or not — pairs with assessedAssetCount so a
 *  report can say how many were skipped (totalAssetCount - assessedAssetCount), typically because
 *  a unit was inaccessible on the walk rather than never assessed at all. */
export function totalAssetCount(assessment: FacilityAssessment): number {
  const systems: SystemData[] = [assessment.controls, assessment.electrical, assessment.mechanical, assessment.plumbing];
  return systems.reduce((n, s) => n + s.assets.length, 0);
}

/* The achievable overall score: 100 points per assessed system type. Mechanical only -> 100,
   mechanical + plumbing -> 200, all four -> 400. Zero when nothing has been assessed. Pair with
   calculateOverallScore (the numerator) for the "X / max" display and with getBuildingRating for
   the scale-aware rating. */
export function maxPossibleScore(assessment: FacilityAssessment): number {
  return activeSystemCount(assessment) * 100;
}

const SYSTEM_LABELS: Record<SystemKey, string> = {
  controls: 'Controls', electrical: 'Electrical', mechanical: 'Mechanical', plumbing: 'Plumbing',
};

export function generateRecommendations(assessment: FacilityAssessment): string[] {
  const recs: string[] = [];
  const keys: SystemKey[] = ['controls', 'electrical', 'mechanical', 'plumbing'];
  for (const key of keys) {
    const sys = assessment[key];
    if (!sys || sys.assets.length === 0) continue;
    const failed = sys.assets.filter(a => a.rating === 'Failed' || a.rating === 'Critical');
    const poor = sys.assets.filter(a => a.rating === 'Poor');
    const aging = sys.assets.filter(a => (a.remainingServiceLife ?? 0) <= 3 && (a.remainingServiceLife ?? 0) > 0);
    if (failed.length) recs.push(`${SYSTEM_LABELS[key]}: ${failed.length} asset(s) Failed/Critical — immediate attention required.`);
    if (poor.length) recs.push(`${SYSTEM_LABELS[key]}: ${poor.length} asset(s) Poor — evaluate within 6–12 months.`);
    if (aging.length) recs.push(`${SYSTEM_LABELS[key]}: ${aging.length} asset(s) have ≤3 years RSL — plan replacement.`);
  }
  if (recs.length === 0) recs.push('All systems satisfactory. Continue regular maintenance.');
  return recs;
}

/* Solid marker fills — for the floor-plan pins, where the colour IS the signal and nothing is
   drawn on top of it. Do NOT put white text on these: they are the old Bootstrap semantic
   palette, and white on Poor (#ffc107) is 1.6:1, Good (#0dcaf0) 2.0:1, Critical (#fd7e14)
   2.6:1. Use RATING_TAG below for anything with a label in it. */
export const RATING_COLORS: Record<RatingLabel, string> = {
  Failed: '#dc3545', Critical: '#fd7e14', Poor: '#ffc107',
  Average: '#6c757d', Good: '#0dcaf0', 'Like New': '#0d6efd', New: '#198754',
};

/* AntD preset tag colours per rating — tinted background, darker text of the same hue, matching
   border. Shared by the facility module and the printed report so a rating reads identically in
   both, and legible either way in print: if the browser drops backgrounds (the default), the
   text is still dark on white rather than white on white. */
export const RATING_TAG: Record<RatingLabel, string> = {
  New: 'green', 'Like New': 'blue', Good: 'cyan', Average: 'default',
  Poor: 'gold', Critical: 'volcano', Failed: 'red',
};

// Descriptive condition statements from the Excel scorecard's Equipment Ratings sheet
export const RATING_DESCRIPTIONS: Record<RatingLabel, string> = {
  New: 'New, recently installed.',
  'Like New': 'No observed or reported issues, no failures expected.',
  Good: 'No reported issues or concerns.',
  Average: 'Minor reported issues with minor concerns.',
  Poor: 'Minor repairs needed to continue operation.',
  Critical: 'Major repairs needed to return to operation.',
  Failed: 'Immediate replacement required.',
};
